import { Image, Skeleton } from '@lounge-fe/ui-kit'
import React, { useMemo } from 'react'
import SelectGroup from '../SelectGroup'
import { LinkedItemSelectionProps } from './types'
import { useFormContext, useWatch } from 'react-hook-form'
import { useVariantUpdate } from '../hooks/useVariantUpdate'
import { isOptionInStock } from '../utils'
import { SelectGroupProps } from '../SelectGroup/types'
import { useDataTranslation } from '@/features/localisation/hooks/useDataTranslation/useDataTranslation'
import { useTranslatedValue } from '../hooks/useTranslatedValue'
import { mapProductOptionLabelOverride } from '@/features/product/utils'

export const LinkedItemSelection = ({
  totalItems,
  name,
  linkedItems,
  isDisabledSelectable,
}: LinkedItemSelectionProps) => {
  const { t } = useDataTranslation()
  const { translateValue } = useTranslatedValue()

  const {
    formState: { errors },
  } = useFormContext()

  const [selectedLinkedItemId, selectedOptions] = useWatch({
    name: [`${name}.id`, `${name}.options`],
  })

  const selectedLinkedItem = useMemo(
    () => linkedItems.find(({ id }) => id === Number(selectedLinkedItemId)),
    [selectedLinkedItemId],
  )

  const memoizedVariants = useMemo(
    () => selectedLinkedItem?.variants ?? [],
    [selectedLinkedItem],
  )

  useVariantUpdate(`${name}.options`, `${name}.variant`, memoizedVariants)

  const hasMultipleLinkedItems = linkedItems.length > 1

  if (!linkedItems.length || !selectedLinkedItem) {
    return null
  }

  return linkedItems.map((linkedItem) => {
    return linkedItem.options.map((option, optionIdx) => {
      const label = mapProductOptionLabelOverride({
        linkedBottom: linkedItem,
        option,
        totalLinkedBottoms: totalItems,
      })

      const fallbackValue = t('product.choose_your_bottoms', {
        defaultValue: option.name,
      })

      const translatedLabel = translateValue({
        preferredValue: label,
        fallbackValue,
        loadingState: (
          <Skeleton>
            <Skeleton.Block className="w-full max-w-[150px] h-5" />
          </Skeleton>
        ),
      })

      return (
        <SelectGroup
          key={`linked-item-selection-${optionIdx}-${option.name}`}
          data-testid="linked-item-selection"
          className="mb-6"
          title={translatedLabel}
          image={
            hasMultipleLinkedItems &&
            selectedLinkedItem.images?.length && (
              <Image
                key={selectedLinkedItem.images[0].src}
                className="object-cover object-center aspect-square w-full"
                data={{
                  src: selectedLinkedItem.images[0].src,
                  width: 136,
                  height: 136,
                  crop: 'center',
                }}
                alt={selectedLinkedItem.title}
              />
            )
          }
          fields={
            [
              hasMultipleLinkedItems && {
                control: 'select',
                name: `${name}.id`,
                testId: `group-${linkedItem.title.toLowerCase().replaceAll(' ', '-')}`,
                label: t('product.product'),
                options: linkedItems.map((linkedItem) => ({
                  label: linkedItem.title,
                  value: linkedItem.id,
                })),
                errors,
              },
              {
                control: hasMultipleLinkedItems ? 'select' : 'choicechip',
                name: `${name}.options[${optionIdx}]`,
                testId: `group-${option.label?.toLowerCase().replaceAll(' ', '-') || option.name.toLowerCase().replaceAll(' ', '-')}`,
                label: option.label,
                isDisabledSelectable,
                placeholder: t('product.select_an_option'),
                options: option.values.map((optionValue) => ({
                  label: optionValue,
                  value: optionValue,
                  disabled: !isOptionInStock(
                    optionValue,
                    optionIdx,
                    selectedOptions,
                    selectedLinkedItem?.variants ?? [],
                  ),
                })),
                errors,
              },
            ].filter(Boolean) as SelectGroupProps['fields']
          }
        />
      )
    })
  })
}
